/*
 * Copyright 2026 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     https://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

/**
 * External dependencies
 */
import { EditorState } from 'draft-js';

/**
 * Internal dependencies
 */
import { LIST_BLOCK_TYPES, MAX_LIST_DEPTH } from './constants';

/**
 * Adjust the nesting depth of every list block in the current selection.
 * Returns null when the selection contains no list block so the browser can
 * retain its normal Tab behavior outside lists.
 *
 * @param {EditorState} editorState Current editor state.
 * @param {number} adjustment Depth adjustment, normally 1 or -1.
 * @return {?EditorState} Updated state, the original state at a depth limit,
 * or null when Tab is not a list command.
 */
function adjustListDepth(editorState, adjustment) {
  const selection = editorState.getSelection();
  const content = editorState.getCurrentContent();
  const blockMap = content.getBlockMap();
  const startKey = selection.getStartKey();
  let endKey = selection.getEndKey();

  if (startKey !== endKey && selection.getEndOffset() === 0) {
    endKey = content.getKeyBefore(endKey) || endKey;
  }

  const selectedBlocks = blockMap
    .toSeq()
    .skipUntil((block, key) => key === startKey)
    .takeUntil((block, key) => key === endKey)
    .concat([[endKey, blockMap.get(endKey)]]);
  const selectedListBlocks = selectedBlocks.filter((block) =>
    LIST_BLOCK_TYPES.includes(block.getType())
  );

  if (selectedListBlocks.isEmpty()) {
    return null;
  }

  if (adjustment > 0) {
    const firstSelectedListKey = selectedListBlocks.keySeq().first();
    const firstSelectedListBlock = selectedListBlocks.first();
    const previousBlock = content.getBlockBefore(firstSelectedListKey);
    const hasValidParent =
      previousBlock &&
      LIST_BLOCK_TYPES.includes(previousBlock.getType()) &&
      previousBlock.getDepth() >= firstSelectedListBlock.getDepth();
    const wouldExceedMaximum = selectedListBlocks.some(
      (block) => block.getDepth() >= MAX_LIST_DEPTH
    );

    // A nested semantic list must have a preceding list item to contain it.
    // Draft permits orphan depths, but HTML export normalizes those back to
    // the root and makes the indentation appear to disappear on blur.
    if (!hasValidParent || wouldExceedMaximum) {
      return editorState;
    }
  }

  let isInSelection = false;
  let hasChangedDepth = false;

  const nextBlockMap = blockMap.map((block, key) => {
    if (key === startKey) {
      isInSelection = true;
    }

    let nextBlock = block;
    if (isInSelection && LIST_BLOCK_TYPES.includes(block.getType())) {
      const nextDepth = Math.max(
        0,
        Math.min(block.getDepth() + adjustment, MAX_LIST_DEPTH)
      );
      if (nextDepth !== block.getDepth()) {
        hasChangedDepth = true;
        nextBlock = block.set('depth', nextDepth);
      }
    }

    if (key === endKey) {
      isInSelection = false;
    }

    return nextBlock;
  });

  if (!hasChangedDepth) {
    return editorState;
  }

  const nextContent = content.merge({
    blockMap: nextBlockMap,
    selectionBefore: selection,
    selectionAfter: selection,
  });

  return EditorState.push(editorState, nextContent, 'adjust-depth');
}

export default adjustListDepth;
